import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = [
  { v: "super_admin", l: "Super Admin" },
  { v: "content_manager", l: "Content Manager" },
  { v: "marketing_manager", l: "Marketing Manager" },
  { v: "support_manager", l: "Support Manager" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "content_manager", active: true });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", password: "", role: "content_manager", active: true }); setEditing("new"); };
  const openEdit = (u) => { setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setEditing(u.id); };

  const save = async () => {
    try {
      if (editing === "new") {
        if (!form.name || !form.email || !form.password) { toast.error("Name, email and password required"); return; }
        await api.post("/users", form);
      } else {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/users/${confirm.id}`); toast.success("User deleted"); setConfirm(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const roleLabel = (r) => ROLES.find((x) => x.v === r)?.l || r;

  return (
    <div>
      <AdminHeader title="User Management & RBAC" subtitle="Manage admin staff and their role-based permissions."
        action={<button onClick={openNew} className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="add-user-btn"><Plus size={16} /> Add User</button>} />
      <Panel>
        <div className="divide-y divide-[#203E70]">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-4 p-4" data-testid={`user-row-${u.id}`}>
              <div className="w-9 h-9 rounded-full bg-[#102653] border border-[#203E70] flex items-center justify-center text-[#D8AE4E] text-sm font-semibold">{(u.name || "?").charAt(0).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{u.name}</p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded border border-[#203E70] gold-text shrink-0">{roleLabel(u.role)}</span>
              <span className={`text-xs shrink-0 ${u.active ? "text-emerald-400" : "text-slate-500"}`}>{u.active ? "Active" : "Disabled"}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(u)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" data-testid={`user-edit-${u.id}`}><Pencil size={15} /></button>
                <button onClick={() => setConfirm(u)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`user-delete-${u.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Add User" : "Edit User"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <TextInput label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="user-form-name" />
            <TextInput label="Email" type="email" value={form.email} disabled={editing !== "new"} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="user-form-email" />
            <TextInput label={editing === "new" ? "Password" : "New Password (leave blank to keep)"} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="user-form-password" />
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="bg-[#06122E] border-[#203E70] text-white" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B1D40] border-[#203E70] text-white">
                  {ROLES.map((r) => <SelectItem key={r.v} value={r.v} data-testid={`role-opt-${r.v}`}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editing !== "new" && (
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="user-form-active" /> Active
              </label>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} className="btn-gold px-4 py-2 text-sm" data-testid="user-form-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <AlertDialogHeader><AlertDialogTitle>Delete user?</AlertDialogTitle><AlertDialogDescription className="text-slate-400">{confirm?.name} ({confirm?.email}) will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#203E70] text-white hover:bg-[#102653]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="user-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
